import { useCallback, useMemo, useState } from "react"

import { LayoutDashboard, Search } from "@/components/icons"
import { useTranslation } from "react-i18next"

import type { SavedDashboard } from "../services/dashboard-service"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import { DashboardCollapsibleSection } from "./dashboard-collapsible-section"
import { DashboardListItem } from "./dashboard-list-item"
import { takeVisibleByPriority } from "../utils/dashboard-helpers"

const DEFAULT_VISIBLE_COUNT = 5

type CollapsedDashboardPopoverProps = {
  className?: string
  dashboards: SavedDashboard[]
  filteredDashboards: SavedDashboard[]
  isLoadingDashboards: boolean
  effectiveSearchTerm: string
  favoriteDashboards: SavedDashboard[]
  companyDashboards: SavedDashboard[]
  personalDashboards: SavedDashboard[]
  isFavoritesSectionOpen: boolean
  isCompanySectionOpen: boolean
  isPersonalSectionOpen: boolean
  isSearching: boolean
  setIsFavoritesSectionOpen: (open: boolean) => void
  setIsCompanySectionOpen: (open: boolean) => void
  setIsPersonalSectionOpen: (open: boolean) => void
  onDashboardClick: (dashboard: SavedDashboard) => void
  onToggleFavorite: (e: React.MouseEvent, dashboardId: string) => void
  onDelete: (dashboard: SavedDashboard | null) => void
  onSearchChange: (term: string) => void
  deleteDialog: React.ReactNode
}

export function CollapsedDashboardPopover({
  className,
  dashboards,
  filteredDashboards,
  isLoadingDashboards,
  effectiveSearchTerm,
  favoriteDashboards,
  companyDashboards,
  personalDashboards,
  isFavoritesSectionOpen,
  isCompanySectionOpen,
  isPersonalSectionOpen,
  isSearching,
  setIsFavoritesSectionOpen,
  setIsCompanySectionOpen,
  setIsPersonalSectionOpen,
  onDashboardClick,
  onToggleFavorite,
  onDelete,
  onSearchChange,
  deleteDialog,
}: CollapsedDashboardPopoverProps) {
  const { t } = useTranslation()

  const [isCollapsedPopoverOpen, setIsCollapsedPopoverOpen] = useState(false)
  const [showAllInCollapsed, setShowAllInCollapsed] = useState(false)

  const { collapsedFavorites, collapsedCompany, collapsedPersonal } = useMemo(() => {
    if (effectiveSearchTerm || showAllInCollapsed) {
      return {
        collapsedFavorites: favoriteDashboards,
        collapsedCompany: companyDashboards,
        collapsedPersonal: personalDashboards,
      }
    }
    const visible = takeVisibleByPriority(DEFAULT_VISIBLE_COUNT, {
      favorites: favoriteDashboards,
      company: companyDashboards,
      personal: personalDashboards,
    })
    return {
      collapsedFavorites: visible.favorites,
      collapsedCompany: visible.company,
      collapsedPersonal: visible.personal,
    }
  }, [
    favoriteDashboards,
    companyDashboards,
    personalDashboards,
    effectiveSearchTerm,
    showAllInCollapsed,
  ])

  const totalVisibleInCollapsed =
    collapsedFavorites.length + collapsedCompany.length + collapsedPersonal.length
  const hasMoreDashboards = filteredDashboards.length > totalVisibleInCollapsed

  const handleCollapsedDashboardClick = useCallback(
    (dashboard: SavedDashboard) => {
      onDashboardClick(dashboard)
      setIsCollapsedPopoverOpen(false)
    },
    [onDashboardClick]
  )

  return (
    <>
      <Popover
        open={isCollapsedPopoverOpen}
        onOpenChange={(open) => {
          setIsCollapsedPopoverOpen(open)
          if (!open) setShowAllInCollapsed(false)
        }}
      >
        <PopoverTrigger asChild>
          <button
            aria-label={t("dash.landing.sidebar.title")}
            type="button"
            className={cn(
              "group/nav-section hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex size-10 items-center justify-center rounded-md transition-colors duration-150",
              className
            )}
          >
            <LayoutDashboard className="size-6" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="flex w-72 flex-col p-0"
          side="right"
          sideOffset={8}
        >
          <div className="border-b p-2">
            <div className="relative">
              <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
              <Input
                aria-label={t("dash.landing.sidebar.search")}
                className="placeholder:text-muted-foreground h-8 pl-9 text-sm"
                placeholder={t("dash.landing.sidebar.search")}
                value={effectiveSearchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && effectiveSearchTerm && onSearchChange("")}
              />
            </div>
          </div>
          <div className="relative flex min-h-0 flex-1 flex-col">
            {showAllInCollapsed && (
              <div className="from-popover pointer-events-none absolute top-0 right-0 left-0 z-10 h-4 bg-gradient-to-b to-transparent" />
            )}
            <div
              className={cn(
                "overflow-y-auto",
                showAllInCollapsed ? "max-h-[300px] pb-10" : "max-h-[300px]"
              )}
            >
              <div className="flex flex-col gap-2 p-1">
                {isLoadingDashboards && (
                  <p className="animate-shimmer from-muted-foreground via-foreground/50 to-muted-foreground bg-gradient-to-r bg-[length:200%_100%] bg-clip-text px-2 py-4 text-center text-xs text-transparent">
                    {t("dash.landing.sidebar.empty")}
                  </p>
                )}
                {!isLoadingDashboards && dashboards.length === 0 && (
                  <p className="text-muted-foreground px-2 py-4 text-center text-xs">
                    {t("dash.landing.sidebar.empty")}
                  </p>
                )}
                {!isLoadingDashboards &&
                  dashboards.length > 0 &&
                  collapsedFavorites.length === 0 &&
                  collapsedCompany.length === 0 &&
                  collapsedPersonal.length === 0 &&
                  effectiveSearchTerm && (
                    <p className="text-muted-foreground px-2 py-4 text-center text-xs">
                      {t("dash.landing.sidebar.no_matches")}
                    </p>
                  )}

                <DashboardCollapsibleSection
                  isOpen={isFavoritesSectionOpen}
                  isSearching={isSearching}
                  items={collapsedFavorites}
                  label={t("dash.landing.favorites")}
                  setOpen={setIsFavoritesSectionOpen}
                  renderItem={(d) => (
                    <DashboardListItem
                      key={d.id}
                      dashboard={d}
                      variant="collapsed"
                      onClick={() => handleCollapsedDashboardClick(d)}
                      onDelete={onDelete}
                      onToggleFavorite={onToggleFavorite}
                    />
                  )}
                />
                <DashboardCollapsibleSection
                  isOpen={isCompanySectionOpen}
                  isSearching={isSearching}
                  items={collapsedCompany}
                  label={t("dash.landing.company_badge")}
                  setOpen={setIsCompanySectionOpen}
                  renderItem={(d) => (
                    <DashboardListItem
                      key={d.id}
                      dashboard={d}
                      variant="collapsed"
                      onClick={() => handleCollapsedDashboardClick(d)}
                      onDelete={onDelete}
                      onToggleFavorite={onToggleFavorite}
                    />
                  )}
                />
                <DashboardCollapsibleSection
                  isOpen={isPersonalSectionOpen}
                  isSearching={isSearching}
                  items={collapsedPersonal}
                  label={t("dash.landing.personal_badge")}
                  setOpen={setIsPersonalSectionOpen}
                  renderItem={(d) => (
                    <DashboardListItem
                      key={d.id}
                      dashboard={d}
                      variant="collapsed"
                      onClick={() => handleCollapsedDashboardClick(d)}
                      onDelete={onDelete}
                      onToggleFavorite={onToggleFavorite}
                    />
                  )}
                />

                {hasMoreDashboards && !effectiveSearchTerm && !showAllInCollapsed && (
                  <Button
                    className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground mt-1 h-7 w-full justify-start px-3 text-xs"
                    variant="ghost"
                    onClick={() => setShowAllInCollapsed(true)}
                  >
                    {`${t("sidebar.see_all")} (${filteredDashboards.length})`}
                  </Button>
                )}
              </div>
            </div>
            {showAllInCollapsed && !effectiveSearchTerm && (
              <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 flex flex-col">
                <div className="from-popover h-6 bg-gradient-to-t to-transparent" />
                <div className="bg-popover px-1 pb-1">
                  <Button
                    className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground pointer-events-auto h-7 w-full justify-start px-3 text-xs"
                    variant="ghost"
                    onClick={() => setShowAllInCollapsed(false)}
                  >
                    {t("sidebar.show_less")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      {deleteDialog}
    </>
  )
}
